/*
 * BRIORA · Ajustes de la auditoría UX (P7, 2026-10-19)
 * Documentación completa: snippets/briora-handoff-personalizaciones.liquid (2.34, 2.36 y 2.38)
 * Cargado desde layout/theme.liquid justo después de briora-premium.js.
 *
 * GEN-14 · Talla sin elegir (anillos y alianzas).
 * El tema marca de entrada la primera talla disponible, así que se podía añadir un anillo al carrito
 * sin haber elegido la talla. Mientras la persona no toque el selector de talla:
 *   - la talla preseleccionada se pinta como no elegida y la leyenda dice «Talla · elige una»;
 *   - «Añadir al carrito» (el de la ficha, el de la barra fija y el de la vista rápida) no añade:
 *     lleva al selector y avisa «Elige tu talla antes de añadir al carrito».
 * Al pulsar cualquier talla (también la preseleccionada) todo vuelve a funcionar como en el tema.
 * No toca la variante ni el formulario del tema: si este archivo no carga, la ficha funciona como antes.
 * Afecta a cualquier producto con una opción «Talla» de más de un valor (hoy, los 81 anillos y alianzas con talla).
 * P8: antes solo se aplicaba si el título decía «anillo» y se quedaban fuera las 2 «Alianza…».
 * Si la URL ya trae ?variant= (enlace a una talla concreta), esa ficha se da por elegida.
 */
(() => {
  const PENDING = 'data-briora-talla-pending';
  const chosen = new Set();

  const mainPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
  if (mainPicker && /[?&]variant=\d/.test(location.search)) chosen.add(mainPicker.dataset.productId);

  const isTallaFieldset = (fieldset) => {
    const legend = fieldset.querySelector('legend');
    if (!legend) return false;
    const name = (legend.firstChild ? legend.firstChild.textContent : legend.textContent).trim().toLowerCase();
    return name.startsWith('talla') && fieldset.querySelectorAll('input[type="radio"]').length > 1;
  };

  const markPending = (fieldset) => {
    if (fieldset.hasAttribute(PENDING)) return;
    fieldset.setAttribute(PENDING, '');
    const legend = fieldset.querySelector('legend');
    if (legend && !legend.querySelector('.briora-talla-hint')) {
      const hint = document.createElement('span');
      hint.className = 'briora-talla-hint';
      hint.textContent = ' · elige una';
      legend.appendChild(hint);
    }
  };

  const init = (root) => {
    if (!(root instanceof Element) && root !== document) return;
    const pickers = root instanceof Element && root.matches('variant-picker') ? [root] : root.querySelectorAll('variant-picker');
    for (const picker of pickers) {
      if (chosen.has(picker.dataset.productId)) continue;
      for (const fieldset of picker.querySelectorAll('fieldset')) {
        if (isTallaFieldset(fieldset)) markPending(fieldset);
      }
    }
  };

  const choose = (fieldset) => {
    const picker = fieldset.closest('variant-picker');
    if (picker) chosen.add(picker.dataset.productId);
    fieldset.removeAttribute(PENDING);
    fieldset.querySelector('.briora-talla-hint')?.remove();
    const next = fieldset.nextElementSibling;
    if (next && next.classList.contains('briora-talla-error')) next.remove();
  };

  const pendingFor = (element) => {
    const scope = element.closest('dialog, .shopify-section, product-card') || document;
    return scope.querySelector(`fieldset[${PENDING}]`);
  };

  const warn = (fieldset) => {
    let message = fieldset.nextElementSibling;
    if (!message || !message.classList.contains('briora-talla-error')) {
      message = document.createElement('p');
      message.className = 'briora-talla-error';
      message.setAttribute('role', 'alert');
      fieldset.insertAdjacentElement('afterend', message);
    }
    message.textContent = '';
    requestAnimationFrame(() => {
      message.textContent = 'Elige tu talla antes de añadir al carrito.';
    });
    fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const input = fieldset.querySelector('input:checked') || fieldset.querySelector('input');
    input?.focus({ preventScroll: true });
  };

  const isAddToCartButton = (button) =>
    (button.closest('product-form-component') && (button.type === 'submit' || button.getAttribute('ref') === 'addToCartButton')) ||
    (button.closest('sticky-add-to-cart') && button.classList.contains('sticky-add-to-cart__button'));

  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const fieldset = target.closest(`fieldset[${PENDING}]`);
      if (fieldset) {
        if (target.closest('label, input')) choose(fieldset);
        return;
      }

      const button = target.closest('button');
      if (!button || !isAddToCartButton(button)) return;
      const pending = pendingFor(button);
      if (!pending) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      warn(pending);
    },
    true
  );

  window.addEventListener(
    'change',
    (event) => {
      const fieldset = event.target instanceof Element ? event.target.closest(`fieldset[${PENDING}]`) : null;
      if (fieldset) choose(fieldset);
    },
    true
  );

  window.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest('product-form-component')) return;
      const pending = pendingFor(form);
      if (!pending) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      warn(pending);
    },
    true
  );

  // La vista rápida y el cambio de variante reutilizan el selector (morph): se vuelve a revisar tras cada cambio.
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      init(document);
    });
  };
  init(document);
  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [PENDING, 'data-product-id'],
  });
})();

/*
 * SAL-05 (P9) · «Vistos recientemente» en la ficha (sections/briora-recently-viewed.liquid).
 * Guarda los productos vistos en localStorage ("brioraViewed", 9 como máximo) y muestra hasta 4,
 * sin el actual, pidiendo la sección a la búsqueda de la tienda. Si no hay ninguno, no se muestra nada.
 */
(() => {
  const box = document.querySelector('[data-briora-recent]');
  if (!box) return;
  const KEY = 'brioraViewed';
  const current = box.dataset.productId;
  let ids = [];
  try {
    ids = JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch (error) {
    ids = [];
  }
  if (!Array.isArray(ids)) ids = [];
  const others = ids.filter((id) => id !== current).slice(0, 4);
  try {
    localStorage.setItem(KEY, JSON.stringify([current, ...ids.filter((id) => id !== current)].slice(0, 9)));
  } catch (error) {
    /* sin almacenamiento: no se recuerda nada */
  }
  if (!others.length) return;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const url = new URL(`${root}search`, location.origin);
  url.searchParams.set('q', others.map((id) => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');
  url.searchParams.set('section_id', 'briora-recently-viewed');

  fetch(url)
    .then((response) => (response.ok ? response.text() : ''))
    .then((html) => {
      const list = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-briora-recent-results]');
      if (!list) return;
      const items = [...list.children].sort(
        (a, b) => others.indexOf(a.dataset.productId) - others.indexOf(b.dataset.productId)
      );
      if (!items.length) return;
      list.replaceChildren(...items);
      box.querySelector('[data-briora-recent-target]').replaceChildren(list);
      box.hidden = false;
    })
    .catch(() => {});
})();

/*
 * CAR-01 (P9) · Aviso en la ficha si ese producto ya está en el carrito: «Esta joya ya está en tu
 * carrito (Talla 14)». Se revisa al cargar y cada vez que cambia el carrito (añadir, cambiar o vaciar).
 */
(() => {
  const form = document.querySelector('main product-form-component');
  if (!form) return;
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const productId = () => {
    const input = form.querySelector('input[name="product-id"]');
    if (input) return Number(input.value);
    const picker = document.querySelector('variant-picker[data-template-product-match="true"]');
    return picker ? Number(picker.dataset.productId) : NaN;
  };

  const update = () => {
    const id = productId();
    if (!id) return;
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        const lines = cart.items.filter((item) => item.product_id === id);
        let note = form.querySelector('.briora-in-cart');
        if (!lines.length) {
          note?.remove();
          return;
        }
        const parts = lines.map((item) => {
          const options = (item.options_with_values || [])
            .filter((option) => option.value && option.value !== 'Default Title')
            .map((option) => `${option.name} ${option.value}`)
            .join(', ');
          const quantity = item.quantity > 1 ? ` ×${item.quantity}` : '';
          return options ? `${options}${quantity}` : quantity.trim();
        }).filter(Boolean);
        if (!note) {
          note = document.createElement('p');
          note.className = 'briora-in-cart';
          note.setAttribute('role', 'status');
          const anchor = form.querySelector('[ref="productFormButtons"]') || form.querySelector('add-to-cart-component');
          if (anchor) anchor.insertAdjacentElement('afterend', note);
          else form.appendChild(note);
        }
        note.textContent = parts.length ? `Esta joya ya está en tu carrito (${parts.join('; ')}).` : 'Esta joya ya está en tu carrito.';
      })
      .catch(() => {});
  };

  let timer;
  const later = () => {
    clearTimeout(timer);
    timer = setTimeout(update, 700);
  };
  update();
  if ('PerformanceObserver' in window) {
    new PerformanceObserver((list) => {
      if (list.getEntries().some((entry) => /\/cart\/(add|change|update|clear)/.test(entry.name))) later();
    }).observe({ type: 'resource', buffered: false });
  }
})();

/*
 * EXT-21 (P11) · Fundido al cambiar de foto en las tarjetas de producto.
 * Al pasar el ratón, el tema salta a la segunda foto sin animación (slideshow.next con animate: false).
 * Aquí, cada vez que una tarjeta cambia de foto (evento slideshow:select), la foto nueva entra con un
 * fundido corto (CSS: product-card slideshow-slide.briora-slide-in). Solo afecta a las tarjetas; la
 * galería de la ficha no cambia. Si este bloque falla, las tarjetas funcionan como en el tema.
 */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  document.addEventListener('slideshow:select', (event) => {
    const slide = event.detail?.slide;
    if (!slide || !slide.closest('product-card')) return;
    slide.classList.remove('briora-slide-in');
    void slide.offsetWidth;
    slide.classList.add('briora-slide-in');
  });
})();
